<script setup lang="ts">
import { storeToRefs } from "pinia";
import { watch } from "vue";
import type LxcError from "~/core/models/LxcError.class";
import { showNotificationSuccess } from "~/core/utils/notifications";
import uploadUtils, {
  type FilesUploadErrorMessagesI,
} from "~/core/utils/upload.utils";
import { useUploadLicense } from "~/modules/license/composables/useUploadLicense";
import { LicenseUploadContext } from "~/modules/license/models/LicenseUploadContext.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";

const props = defineProps<{
  show: boolean;
  uploadContext: LicenseUploadContext;
}>();

const emits = defineEmits(["update:show", "isLoading", "error", "success"]);

const { t } = useI18n();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { isLoading, error, uploadLicense } = useUploadLicense(
  currentOrganization.value?.code,
);

const files: Ref<FileList | undefined | null> = ref();
const fileProgress: Ref<number | undefined | null> = ref();
const filesUploadError: Ref<LxcError | undefined | null> = ref();
const fileError: Ref<Error | undefined | null> = ref();
const fileSelected: ComputedRef<boolean> = computed(
  () => !!files.value?.length,
);
const fileFormat = [".jwt", ".opl"];
const MAX_FILE_SIZE_BYTES_ALLOWED = 750_000_000;
const errorMessages: FilesUploadErrorMessagesI = {
  invalidType: t("license.upload.validation.fileMandatory"),
  empty: t("license.upload.validation.fileEmpty"),
  size: t("license.upload.validation.fileSize"),
};

const uploadLicenseFile = async () => {
  filesUploadError.value =
    filesUploadError.value !== undefined ? null : undefined;
  fileError.value = fileError.value !== undefined ? null : undefined;
  if (
    files.value &&
    files.value.length &&
    uploadUtils.canUploadFile(
      files.value[0],
      MAX_FILE_SIZE_BYTES_ALLOWED,
      fileFormat,
      errorMessages,
      fileError,
    )
  ) {
    if (props.uploadContext === LicenseUploadContext.OVERLAY) {
      emits("update:show", false);
    }
    await uploadLicense(files.value[0], fileProgress);

    if (error.value) {
      filesUploadError.value = error.value;
    } else {
      showNotificationSuccess(t("license.upload.success"));
      emits("success");
    }
    if (props.uploadContext === LicenseUploadContext.LICENSE_PAGE) {
      emits("update:show", false);
    }
  }
};

const reset = () => {
  files.value = undefined;
  fileProgress.value = undefined;
  filesUploadError.value = undefined;
  fileError.value = undefined;
};

const onClose = () => {
  reset();
  emits("update:show", false);
};

watch(
  () => isLoading.value,
  (newValue) => emits("isLoading", newValue),
);
watch(
  () => props.show,
  () => {
    if (props.show) {
      reset();
    }
  },
);
watch(
  () => filesUploadError.value,
  (newValue) => emits("error", newValue),
);
</script>

<template>
  <lxc-common-modal
    :show="show"
    body-class="bg-white"
    header-class="!justify-start"
    footer-class="!flex-row-reverse gap-3"
    title-class="!mt-6"
    class="!w-[34rem]"
    no-close-button
    @close="onClose"
  >
    <template #header>
      <h4>{{ t("license.upload.new.text") }}</h4>
    </template>
    <template #body>
      <upload-file
        v-model:files="files"
        v-model:files-progress="fileProgress"
        v-model:files-upload-error="fileError"
        :file-formats="fileFormat"
      />
    </template>
    <template #footer>
      <lxc-button
        :title="t('button.upload')"
        :text="t('button.upload')"
        :disabled="!fileSelected"
        :is-loading="
          uploadContext === LicenseUploadContext.LICENSE_PAGE
            ? isLoading
            : false
        "
        @click="uploadLicenseFile"
      />
      <lxc-button
        :title="t('button.cancel')"
        :text="t('button.cancel')"
        type="tertiary"
        @click="onClose"
      />
    </template>
  </lxc-common-modal>
</template>
